import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bot,
  FileSpreadsheet,
  Loader2,
  Lock,
  Mail,
  MessageCircle,
  Plus,
  Plug,
  RefreshCw,
  Sparkles,
  Trash2,
} from 'lucide-react';
import Button from '../../components/shared/Button';
import Input from '../../components/shared/Input';
import { VendorPageHeader, VendorGuideCallout } from '../../components/vendor/guidance';
import { VENDOR_PAGE_GUIDES } from '../../utils/vendorPageGuides';
import { useToastContext } from '../../contexts/ToastContext';
import {
  useSyncVendorSheets,
  useUpdateVendorIntegrations,
  useVendorIntegrations,
} from '../../services/vendorIntegrationsService';

const emptyForm = {
  whatsapp: {
    phone: '',
    display_name: '',
    enabled: false,
    notify_on_automation: true,
    templates: [],
  },
  sheets: {
    sheet_url: '',
    enabled: false,
    auto_export_weekly: false,
    two_way_enabled: false,
  },
  agents: { enabled: false, prefer_board_drafts: true },
  digest: { enabled: true },
};

const newTemplate = () => ({
  id: `custom_${Date.now()}`,
  name: 'Custom template',
  body: 'Hi {{name}}, … — {{business_name}}',
  built_in: false,
});

const VendorIntegrations = () => {
  const { success, error: showError } = useToastContext();
  const { data: res, isLoading } = useVendorIntegrations();
  const payload = res?.data || {};
  const caps = payload.capabilities || {};
  const tierLabel = payload.tier_label || 'Free';

  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    const integ = payload.integrations;
    if (!integ) return;
    setForm({
      whatsapp: {
        phone: integ.whatsapp?.phone || '',
        display_name: integ.whatsapp?.display_name || '',
        enabled: Boolean(integ.whatsapp?.enabled),
        notify_on_automation: integ.whatsapp?.notify_on_automation !== false,
        templates: Array.isArray(integ.whatsapp?.templates) ? integ.whatsapp.templates : [],
      },
      sheets: {
        sheet_url: integ.sheets?.sheet_url || '',
        enabled: Boolean(integ.sheets?.enabled),
        auto_export_weekly: Boolean(integ.sheets?.auto_export_weekly),
        two_way_enabled: Boolean(integ.sheets?.two_way_enabled),
      },
      agents: {
        enabled: Boolean(integ.agents?.enabled),
        prefer_board_drafts: integ.agents?.prefer_board_drafts !== false,
      },
      digest: {
        enabled: integ.digest?.enabled !== false,
      },
    });
  }, [payload.integrations]);

  const saveMutation = useUpdateVendorIntegrations({
    onSuccess: () => success(null, 'Integrations saved.'),
    onError: (err) =>
      showError(
        err?.response?.data?.message || 'Could not save integrations',
        err?.response?.data?.upgrade_required
          ? 'Upgrade your plan to enable this connection.'
          : undefined
      ),
  });

  const syncSheetsMutation = useSyncVendorSheets({
    onSuccess: (res) => success(null, res?.message || 'Sheet synced.'),
    onError: (err) =>
      showError(
        err?.response?.data?.message || 'Could not sync Google Sheet',
        err?.response?.data?.upgrade_required
          ? 'Upgrade to Pro for live Sheets sync.'
          : undefined
      ),
  });

  const statusBadge = (status, unlocked) => {
    if (!unlocked) {
      return (
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
          <Lock className="h-3 w-3" />
          Locked
        </span>
      );
    }
    const connected = status === 'configured' || status === 'ready';
    return (
      <span
        className={`text-[10px] font-semibold uppercase tracking-wide ${
          connected ? 'text-emerald-700' : 'text-ink-tertiary'
        }`}
      >
        {connected ? 'Connected' : 'Not connected'}
      </span>
    );
  };

  const updateTemplate = (id, field, value) => {
    setForm((p) => ({
      ...p,
      whatsapp: {
        ...p.whatsapp,
        templates: (p.whatsapp.templates || []).map((t) =>
          t.id === id ? { ...t, [field]: value } : t
        ),
      },
    }));
  };

  const removeTemplate = (id, builtIn) => {
    if (builtIn) {
      showError(null, 'Built-in templates can be edited but not deleted.');
      return;
    }
    setForm((p) => ({
      ...p,
      whatsapp: {
        ...p.whatsapp,
        templates: (p.whatsapp.templates || []).filter((t) => t.id !== id),
      },
    }));
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-24">
        <Loader2 className="h-7 w-7 animate-spin text-ink" />
      </div>
    );
  }

  const waStatus = payload.integrations?.whatsapp?.status;
  const sheetsStatus = payload.integrations?.sheets?.status;
  const agentsStatus = payload.integrations?.agents?.status;
  const sheetsMeta = payload.sheets_meta || {};
  const sheetsLastSync = payload.integrations?.sheets?.last_synced_at;
  const sheetsLastMsg = payload.integrations?.sheets?.last_sync_message;
  const variables = payload.integrations?.whatsapp?.variables || [
    '{{name}}',
    '{{title}}',
    '{{business_name}}',
    '{{meet_link}}',
    '{{starts_at}}',
  ];

  return (
    <div className="space-y-6 pb-12">
      <VendorPageHeader
        title="Integrations"
        subtitle="Connect WhatsApp, Sheets, and agents so Automations can reach the right places — tiered by plan."
        actions={
          <span className="inline-flex items-center gap-1.5 rounded-md border border-border bg-surface px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-ink-secondary">
            <Sparkles className="h-3.5 w-3.5" />
            {tierLabel}
          </span>
        }
      />

      <VendorGuideCallout
        title={VENDOR_PAGE_GUIDES.integrations?.title || 'Connect once, reuse everywhere'}
        steps={
          VENDOR_PAGE_GUIDES.integrations?.steps || [
            'Connect your WhatsApp business number and edit message templates (Pro).',
            'Paste a Google Sheets URL, share with the platform account, Sync now (Pro).',
            'Enable agents so Workspace can draft follow-ups you approve (Pro).',
          ]
        }
        icon={Plug}
      />

      <section className="rounded-product-md border border-border bg-surface p-4 sm:p-5 space-y-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <MessageCircle className="h-4 w-4 text-ink-tertiary" />
            <div>
              <h2 className="text-sm font-semibold text-ink">WhatsApp</h2>
              <p className="text-xs text-ink-secondary">
                Connect a business number and manage templates used from Workspace drafts.
                Nothing auto-sends — you always open WhatsApp yourself.
              </p>
            </div>
          </div>
          {statusBadge(waStatus, caps.can_whatsapp)}
        </div>

        {!caps.can_whatsapp ? (
          <p className="rounded-md border border-dashed border-border bg-surface-muted/40 px-3 py-2 text-xs text-ink-secondary">
            <Lock className="mr-1 inline h-3 w-3" />
            Pro required.{' '}
            <Link
              to="/vendor/subscription"
              className="font-medium text-ink underline-offset-2 hover:underline"
            >
              Upgrade
            </Link>
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Input
                label="Business phone *"
                value={form.whatsapp.phone}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    whatsapp: { ...p.whatsapp, phone: e.target.value },
                  }))
                }
                placeholder="+1 555 000 0000"
              />
              <Input
                label="Sender display name"
                value={form.whatsapp.display_name}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    whatsapp: { ...p.whatsapp, display_name: e.target.value },
                  }))
                }
                placeholder="Shown as {{business_name}} fallback"
              />
            </div>
            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-2 text-xs text-ink">
                <input
                  type="checkbox"
                  checked={form.whatsapp.enabled}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      whatsapp: { ...p.whatsapp, enabled: e.target.checked },
                    }))
                  }
                />
                Mark as connected
              </label>
              <label className="flex items-center gap-2 text-xs text-ink">
                <input
                  type="checkbox"
                  checked={form.whatsapp.notify_on_automation}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      whatsapp: {
                        ...p.whatsapp,
                        notify_on_automation: e.target.checked,
                      },
                    }))
                  }
                />
                Notify when a recipe fires
              </label>
            </div>

            <div className="border-t border-border pt-4 space-y-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-ink">Message templates</h3>
                  <p className="text-xs text-ink-tertiary">
                    Variables:{' '}
                    {variables.map((v) => (
                      <code
                        key={v}
                        className="mr-1 rounded bg-surface-muted px-1 py-0.5 text-[10px] text-ink"
                      >
                        {v}
                      </code>
                    ))}
                  </p>
                </div>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-md"
                  icon={Plus}
                  onClick={() =>
                    setForm((p) => ({
                      ...p,
                      whatsapp: {
                        ...p.whatsapp,
                        templates: [...(p.whatsapp.templates || []), newTemplate()],
                      },
                    }))
                  }
                  disabled={(form.whatsapp.templates || []).length >= 20}
                >
                  Add template
                </Button>
              </div>

              <div className="space-y-3">
                {(form.whatsapp.templates || []).map((t) => (
                  <div
                    key={t.id}
                    className="rounded-md border border-border bg-surface-muted/40 p-3 space-y-2"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Input
                        label="Name"
                        value={t.name}
                        onChange={(e) => updateTemplate(t.id, 'name', e.target.value)}
                        className="flex-1 min-w-[160px]"
                      />
                      {t.built_in ? (
                        <span className="mt-5 text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
                          Built-in
                        </span>
                      ) : (
                        <button
                          type="button"
                          className="mt-5 inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700"
                          onClick={() => removeTemplate(t.id, t.built_in)}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          Remove
                        </button>
                      )}
                    </div>
                    <div>
                      <label className="mb-1 block text-sm font-medium text-ink-secondary">
                        Message body
                      </label>
                      <textarea
                        className="w-full rounded-xl border border-gray-300 bg-surface px-3 py-2 text-sm text-ink"
                        rows={3}
                        value={t.body}
                        onChange={(e) => updateTemplate(t.id, 'body', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <section className="rounded-product-md border border-border bg-surface p-4 sm:p-5 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="h-4 w-4 text-ink-tertiary" />
              <h2 className="text-sm font-semibold text-ink">Excel / Google Sheets</h2>
            </div>
            {statusBadge(sheetsStatus, caps.can_sheets_live || caps.can_excel_export)}
          </div>
          <p className="text-xs text-ink-secondary">
            Live sync pushes Workspace + Bookings tabs into your Google Sheet (Pro).
            Optional Inbox pull creates Workspace todos from sheet rows. CSV export
            still works from Workspace (Basic+).
          </p>
          {!caps.can_sheets_live ? (
            <p className="rounded-md border border-dashed border-border bg-surface-muted/40 px-3 py-2 text-xs text-ink-secondary">
              <Lock className="mr-1 inline h-3 w-3" />
              Live Sheets sync requires Pro.{' '}
              {caps.can_excel_export ? (
                <>CSV/Excel export is already available on Insights and Workspace.</>
              ) : (
                <Link
                  to="/vendor/subscription"
                  className="font-medium text-ink underline-offset-2 hover:underline"
                >
                  Upgrade
                </Link>
              )}
            </p>
          ) : (
            <>
              <Input
                label="Google Sheets URL"
                value={form.sheets.sheet_url}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    sheets: { ...p.sheets, sheet_url: e.target.value },
                  }))
                }
                placeholder="https://docs.google.com/spreadsheets/d/…"
              />
              {sheetsMeta.share_email ? (
                <p className="text-[11px] text-ink-secondary">
                  Share the workbook with{' '}
                  <span className="font-medium text-ink">{sheetsMeta.share_email}</span>{' '}
                  as Editor, then Sync now.
                </p>
              ) : (
                <p className="text-[11px] text-ink-secondary">
                  Share the workbook with the platform Google account as Editor (ask your
                  admin if unsure), then Sync now.
                </p>
              )}
              {!sheetsMeta.api_configured ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                  Server Sheets credentials are not configured yet. You can save the URL;
                  Sync will work once an admin sets GOOGLE_SHEETS_* env vars.
                </p>
              ) : null}
              <label className="flex items-center gap-2 text-xs text-ink">
                <input
                  type="checkbox"
                  checked={form.sheets.enabled}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      sheets: { ...p.sheets, enabled: e.target.checked },
                    }))
                  }
                />
                Enable live sync to this sheet
              </label>
              <label className="flex items-center gap-2 text-xs text-ink">
                <input
                  type="checkbox"
                  checked={form.sheets.auto_export_weekly}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      sheets: { ...p.sheets, auto_export_weekly: e.target.checked },
                    }))
                  }
                />
                Auto-sync every Monday morning
              </label>
              <label className="flex items-center gap-2 text-xs text-ink">
                <input
                  type="checkbox"
                  checked={form.sheets.two_way_enabled}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      sheets: { ...p.sheets, two_way_enabled: e.target.checked },
                    }))
                  }
                />
                Two-way: import Inbox tab rows into Workspace
              </label>
              {form.sheets.two_way_enabled ? (
                <p className="text-[11px] text-ink-secondary">
                  Add rows on the <span className="font-medium text-ink">Inbox</span> tab
                  with columns title, type, priority, channel, due_at. Leave
                  import_status empty (or pending). Sync marks them imported so they
                  are not pulled twice. Workspace / Bookings tabs stay push-only.
                </p>
              ) : null}
              {sheetsLastSync ? (
                <p className="text-[11px] text-ink-tertiary">
                  Last sync {new Date(sheetsLastSync).toLocaleString()}
                  {sheetsLastMsg ? ` — ${sheetsLastMsg}` : ''}
                </p>
              ) : null}
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={
                  syncSheetsMutation.isPending ||
                  !form.sheets.enabled ||
                  !form.sheets.sheet_url.trim()
                }
                onClick={() => syncSheetsMutation.mutate()}
              >
                {syncSheetsMutation.isPending ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <RefreshCw className="h-3.5 w-3.5" />
                )}
                <span className="ml-1.5">Sync now</span>
              </Button>
            </>
          )}
        </section>

        <section className="rounded-product-md border border-border bg-surface p-4 sm:p-5 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2">
              <Bot className="h-4 w-4 text-ink-tertiary" />
              <h2 className="text-sm font-semibold text-ink">Workflow agents</h2>
            </div>
            {statusBadge(agentsStatus, caps.can_agents)}
          </div>
          <p className="text-xs text-ink-secondary">
            Agents draft board tasks and messages — you always confirm before send.
          </p>
          {!caps.can_agents ? (
            <p className="rounded-md border border-dashed border-border bg-surface-muted/40 px-3 py-2 text-xs text-ink-secondary">
              <Lock className="mr-1 inline h-3 w-3" />
              Pro required.{' '}
              <Link
                to="/vendor/subscription"
                className="font-medium text-ink underline-offset-2 hover:underline"
              >
                Upgrade
              </Link>
            </p>
          ) : (
            <>
              <label className="flex items-center gap-2 text-xs text-ink">
                <input
                  type="checkbox"
                  checked={form.agents.enabled}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      agents: { ...p.agents, enabled: e.target.checked },
                    }))
                  }
                />
                Enable agents on Workspace
              </label>
              <label className="flex items-center gap-2 text-xs text-ink">
                <input
                  type="checkbox"
                  checked={form.agents.prefer_board_drafts}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      agents: { ...p.agents, prefer_board_drafts: e.target.checked },
                    }))
                  }
                />
                Prefer drafts as board cards
              </label>
            </>
          )}
        </section>
      </div>

      <section className="rounded-product-md border border-border bg-surface p-4 sm:p-5 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-ink-tertiary" />
            <h2 className="text-sm font-semibold text-ink">Weekly Workspace digest</h2>
          </div>
          {caps.can_automations ? (
            <span className="text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
              Pro
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-ink-tertiary">
              <Lock className="h-3 w-3" />
              Locked
            </span>
          )}
        </div>
        <p className="text-xs text-ink-secondary">
          Monday morning email with completed tasks, overdue work, automation runs, and jobs
          ahead. Counts toward your plan email quota.
        </p>
        {!caps.can_automations ? (
          <p className="rounded-md border border-dashed border-border bg-surface-muted/40 px-3 py-2 text-xs text-ink-secondary">
            <Lock className="mr-1 inline h-3 w-3" />
            Pro required.{' '}
            <Link
              to="/vendor/subscription"
              className="font-medium text-ink underline-offset-2 hover:underline"
            >
              Upgrade
            </Link>
          </p>
        ) : (
          <>
            <label className="flex items-center gap-2 text-xs text-ink">
              <input
                type="checkbox"
                checked={form.digest.enabled}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    digest: { ...p.digest, enabled: e.target.checked },
                  }))
                }
              />
              Send weekly digest to my account email
            </label>
            {payload.integrations?.digest?.last_sent_at ? (
              <p className="text-[11px] text-ink-tertiary">
                Last sent {payload.integrations.digest.last_sent_at}
              </p>
            ) : null}
          </>
        )}
      </section>

      <div className="flex flex-wrap items-center gap-3">
        <Button
          className="rounded-md"
          loading={saveMutation.isPending}
          onClick={() => saveMutation.mutate(form)}
        >
          Save connections
        </Button>
        <Link
          to="/vendor/automations"
          className="text-xs font-medium text-ink underline-offset-2 hover:underline"
        >
          Back to Automations →
        </Link>
        <Link
          to="/vendor/workspace"
          className="text-xs font-medium text-ink underline-offset-2 hover:underline"
        >
          Open Workspace →
        </Link>
      </div>
    </div>
  );
};

export default VendorIntegrations;
